import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Package, RefreshCw, Eye, UserCheck, Ban, Clock, Truck, CheckCircle, XCircle, AlertTriangle, Filter, X, Loader2, ChevronDown, MapPin, Navigation, User, ArrowRight, History, Award, Zap, Info } from 'lucide-react';
import DataGrid from '../../components/datagrid/DataGrid';
import MarketplaceBadge from '../../components/common/MarketplaceBadge';
import { getDeliveryChannel } from '../../utils/marketplace';
import { DELIVERY_STATUS, TERMINAL_STATUSES } from '../../utils/deliveryStatus';
import DeliveryRouteMap from './DeliveryRouteMap';
import MiniLocationMap from './MiniLocationMap';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS, FLEET_HUB_METHODS, FLEET_APP_EVENTS, emitFleetAppEvent } from '../../config/environment';
import { fleetToast } from '../../utils/toast';
import { formatTL } from '../../utils/money';
import './DeliveriesPage.css';

/* Sekmenin hangi durumları kapsadığı SUNUCUDA tanımlıdır (FleetDeliveryTabs): istemci yalnız sekme
   anahtarını gönderir. Burada durum listesi tutulduğu sürece sayaç ile liste kayıyordu — "Partner
   Hazırlıyor" paketi sayaçta Atama Bekleyen'e, listede Aktif'e düşüp hiçbir sekmede görünmüyordu. */
const TAB_CONFIG = [
    { key: 'pending', label: 'Atama Bekleyen', icon: 'Clock', color: '#f59e0b' },
    { key: 'active', label: 'Aktif', icon: 'Truck', color: '#3b82f6' },
    { key: 'completed', label: 'Tamamlanan', icon: 'CheckCircle', color: '#10b981' }
];

/* Durum etiketleri paylaşılan haritadan gelir: rapor ekranı da aynı adları yazsın. */
const STATUS_MAP = DELIVERY_STATUS;

const DeliveriesPage = () => {
    const [activeTab, setActiveTab] = useState('pending');
    const [tabCounts, setTabCounts] = useState({});
    const [critical, setCritical] = useState(null);

    const [deliveries, setDeliveries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalRecords, setTotalRecords] = useState(0);

    const [sortField, setSortField] = useState('CreatedAtUtc');
    const [sortOrder, setSortOrder] = useState('desc');

    const [filters, setFilters] = useState({});
    const [filterTypes, setFilterTypes] = useState({});

    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [advancedFilters, setAdvancedFilters] = useState({
        status: '',
        searchTerm: '',
        startDate: '',
        endDate: '',
        /** '' = tüm kuryeler · 'unassigned' = kurye atanmamış · aksi halde CourierId */
        courierId: '',
        /** '' = tüm restoranlar · aksi halde PartnerId (yalnız filonun kendi restoranları) */
        partnerId: ''
    });

    const [selectedDelivery, setSelectedDelivery] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [routePoints, setRoutePoints] = useState(null);
    const [assignModal, setAssignModal] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    // Gecikme rozetlerinin canlı sayaçları için 60sn'de bir yeniden render tetikler.
    const [nowTick, setNowTick] = useState(Date.now());
    const [members, setMembers] = useState([]);
    /** Restoran filtresi seçenekleri — sunucudan gelir, filonun kendi restoranlarıyla sınırlıdır. */
    const [partnerOptions, setPartnerOptions] = useState([]);
    const [selectedCourierId, setSelectedCourierId] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);

    const formatCurrency = (amount) => {
        if (amount == null) return '-';
        return formatTL(amount);
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleString('tr-TR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDistance = (meters) => {
        if (!meters) return '-';
        if (meters < 1000) return `${meters} m`;
        return `${(meters / 1000).toFixed(1)} km`;
    };

    const isTerminal = (statusCode) => TERMINAL_STATUSES.includes(statusCode);

    // CRM/partner ile aynı: dostça sipariş no (TL-260704-1) öncelikli; yoksa ham external/order id.
    const orderNo = (row) => row?.internalOrderNumber || row?.externalOrderId || '-';

    // Gecikme rozeti — backend gecikme tarayıcısıyla AYNI mantık (kod sabiti eşikler).
    // Öncelik: atanamadı > hazır-alınmadı > söz-aşıldı. Terminal statüde rozet yok.
    const DELAY_READY_MIN = 5;
    const DELAY_UNASSIGNED_MIN = 3;
    const minsSince = (dateStr) => {
        if (!dateStr) return null;
        return Math.max(0, Math.floor((nowTick - new Date(dateStr).getTime()) / 60000));
    };
    const computeDelay = (row) => {
        if (!row || isTerminal(row.statusCode)) return null;
        const cs = row.statusCode;

        // 1) Kurye bulunamadı, atanmadan bekliyor
        if (!row.assignedCourierId) {
            const m = minsSince(row.createdAtUtc);
            if (m != null && m >= DELAY_UNASSIGNED_MIN)
                return { kind: 3, tone: 'danger', label: `Atanmadı • ${m} dk` };
        }
        // 2) Restoran hazır dedi ama henüz teslim alınmadı (5,6 = alındı/teslimatta)
        if (row.kitchenReadyAtUtc && ![5, 6].includes(cs)) {
            const m = minsSince(row.kitchenReadyAtUtc);
            if (m != null && m >= DELAY_READY_MIN)
                return { kind: 1, tone: 'warn', label: `Hazır • alınmadı ${m} dk` };
        }
        // 3) Söz verilen teslim süresi aşıldı
        if (row.promisedAtUtc && new Date(row.promisedAtUtc).getTime() < nowTick) {
            const m = minsSince(row.promisedAtUtc);
            return { kind: 2, tone: 'danger', label: m != null && m > 0 ? `Gecikti • ${m} dk` : 'Gecikti' };
        }
        return null;
    };

    const timelineSteps = useMemo(() => {
        if (!selectedDelivery) return [];
        const cs = selectedDelivery.statusCode ?? 0;
        const history = selectedDelivery.history || [];
        const reached = new Map();

        if (history.length > 0) {
            reached.set(history[0].fromStatus, selectedDelivery.createdAtUtc);
            for (const h of history) {
                if (!reached.has(h.toStatus)) reached.set(h.toStatus, h.changedAtUtc);
            }
        }
        if (!reached.has(cs)) reached.set(cs, null);

        const defs = [
            [0, 'Sipariş Oluşturuldu', Clock, true, 'createdAtUtc'],
            [10, 'Partner Hazırlıyor', Clock, false],
            [11, 'Yetersiz Bakiye', AlertTriangle, false],
            [1, 'Kurye Aranıyor', Truck, false],
            [2, 'Kurye Onayı Bekleniyor', Clock, false],
            [3, 'Kurye Atandı', User, true, 'assignedAtUtc'],
            [4, 'Teslimat Alınıyor', Truck, false],
            [5, 'Teslimat Alındı', Package, true, 'pickedUpAtUtc'],
            [6, 'Teslimatta', Truck, false],
            [7, 'Teslim Edildi', CheckCircle, true, 'deliveredAtUtc'],
        ];

        const isFailed = cs === 8;
        const isCancelled = cs === 9;
        const isTerminalStatus = isFailed || isCancelled;

        let filtered;
        if (isTerminalStatus) {
            filtered = defs.filter(d => reached.has(d[0]));
            filtered.push(isFailed
                ? [8, 'Başarısız', XCircle, false, null, 'failed']
                : [9, 'İptal Edildi', XCircle, false, null, 'cancelled']);
        } else {
            filtered = defs.filter(d => d[3] || reached.has(d[0]));
        }

        return filtered.map((d, i) => {
            const [st, label, Icon, , tf, terminal] = d;
            const isLast = i === filtered.length - 1;
            let state;

            if (terminal) state = terminal;
            else if (st === cs) state = isTerminalStatus ? 'completed' : 'active';
            else if (reached.has(st)) state = 'completed';
            else state = 'pending';

            const time = tf ? selectedDelivery[tf] : (reached.get(st) || null);

            return { st, label, Icon, state, time, isLast };
        });
    }, [selectedDelivery]);

    const buildFilterModel = useCallback((statusArray) => {
        const filterModel = {};

        Object.keys(filters).forEach((key) => {
            if (filters[key] !== undefined && filters[key] !== null && String(filters[key]).trim() !== '') {
                filterModel[key] = {
                    filter: filters[key],
                    type: filterTypes[key] || 'contains',
                    caseSensitive: false
                };
            }
        });

        if (statusArray && statusArray.length > 0) {
            filterModel['status'] = {
                filter: statusArray.join(','),
                type: 'in',
                caseSensitive: false
            };
        }

        /* Kurye filtresi: seçili kuryenin paketleri ya da hiç kurye atanmamış paketler.
           Kolon başlığındaki "Kurye" arama kutusu kurye ADInı metin olarak arıyor; bu filtre
           ID üzerinden çalışır, isim benzerliğinden etkilenmez. */
        if (advancedFilters.courierId === 'unassigned') {
            filterModel['assignedCourierId'] = { filter: '', type: 'null' };
        } else if (advancedFilters.courierId) {
            filterModel['assignedCourierId'] = {
                filter: advancedFilters.courierId,
                type: 'eq',
                caseSensitive: true
            };
        }

        /* Restoran filtresi ID üzerinden çalışır — seçenekleri sunucu filonun KENDİ
           teslimatlarından türetiyor, dolayısıyla listede olmayan bir restoran seçilemez
           ve seçilse bile görünürlük koşulu kesişimi boşaltır. */
        if (advancedFilters.partnerId) {
            filterModel['partnerId'] = {
                filter: advancedFilters.partnerId,
                type: 'eq',
                caseSensitive: true
            };
        }

        return Object.keys(filterModel).length > 0 ? filterModel : null;
    }, [filters, filterTypes, advancedFilters.courierId, advancedFilters.partnerId]);

    const loadDeliveries = useCallback(async (tabKey) => {
        setLoading(true);
        setError('');

        try {
            const targetTab = TAB_CONFIG.find(t => t.key === tabKey) || TAB_CONFIG[0];

            // Gelişmiş filtreden tek durum seçildiyse o durum uygulanır ve sekme gönderilmez;
            // aksi halde kapsamı sunucudaki sekme tanımı belirler.
            const singleStatus = advancedFilters.status !== '' ? parseInt(advancedFilters.status) : null;
            const filterModel = buildFilterModel(singleStatus !== null ? [singleStatus] : null);

            const requestBody = {
                page: currentPage,
                pageSize: pageSize,
                sortModels: sortField ? [{ colId: sortField, sort: sortOrder }] : null,
                filterModel: filterModel,
                tab: singleStatus !== null ? null : targetTab.key
            };

            if (advancedFilters.searchTerm) {
                requestBody.searchTerm = advancedFilters.searchTerm;
            }
            if (advancedFilters.startDate) {
                requestBody.startDate = advancedFilters.startDate;
            }
            if (advancedFilters.endDate) {
                requestBody.endDate = advancedFilters.endDate;
            }

            const result = await apiService.post(API_ENDPOINTS.FLEET_PANEL.DELIVERIES_LIST, requestBody);

            if (result.success) {
                setDeliveries(result.data?.items || []);
                setTotalRecords(result.data?.totalCount || 0);
            } else {
                setError(result.error || 'Veriler yüklenemedi');
            }
        } catch (err) {
            setError('Bir hata oluştu');
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, sortField, sortOrder, buildFilterModel, advancedFilters]);

    const loadTabCounts = useCallback(async () => {
        try {
            const response = await apiService.get(API_ENDPOINTS.FLEET_PANEL.DELIVERY_TAB_COUNTS);
            if (response.success && response.data) {
                setTabCounts({
                    pending: response.data.pending || 0,
                    active: response.data.active || 0,
                    completed: response.data.completed || 0,
                });
            }
        } catch (e) { }
    }, []);

    const loadCritical = useCallback(async () => {
        try {
            const res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.DELIVERY_CRITICAL_SUMMARY);
            if (res.success && res.data) setCritical(res.data);
        } catch (e) { }
    }, []);

    const loadMembers = useCallback(async () => {
        try {
            const response = await apiService.post(API_ENDPOINTS.FLEET_PANEL.MEMBERS_LIST, {
                page: 1,
                pageSize: 100,
                filterModel: {
                    IsActive: { filter: 'true', type: 'eq' }
                }
            });
            if (response.success) {
                setMembers(response.data?.items || []);
            }
        } catch (e) { }
    }, []);

    const loadPartnerOptions = useCallback(async () => {
        try {
            const response = await apiService.get(API_ENDPOINTS.FLEET_PANEL.DELIVERY_PARTNERS);
            if (response.success) setPartnerOptions(response.data || []);
        } catch (e) { }
    }, []);

    const handleTabChange = (tabKey) => {
        if (tabKey !== activeTab) {
            setCurrentPage(1);
            setActiveTab(tabKey);
        }
    };

    const handleSort = (field, order) => {
        setSortField(field);
        setSortOrder(order);
    };

    const handleFilterChange = (field, value, type) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
        setFilterTypes((prev) => ({ ...prev, [field]: type }));
        setCurrentPage(1);
    };

    const handlePageSizeChange = (size) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    const handleAdvancedFilterChange = (field, value) => {
        setAdvancedFilters(prev => ({ ...prev, [field]: value }));
    };

    const applyAdvancedFilters = () => {
        setCurrentPage(1);
    };

    const clearAdvancedFilters = () => {
        setAdvancedFilters({ status: '', searchTerm: '', startDate: '', endDate: '', courierId: '', partnerId: '' });
        setCurrentPage(1);
    };

    const openDetail = async (row) => {
        try {
            setDetailLoading(true);
            setSelectedDelivery(row);
            setRoutePoints(null);
            const response = await apiService.get(API_ENDPOINTS.FLEET_PANEL.DELIVERY_DETAIL(row.id));
            const d = response.success ? response.data : row;
            if (response.success) setSelectedDelivery(response.data);

            // Restoran→müşteri yol rotası (harita için). Gelmezse harita düz çizgi çizer.
            const { pickupLatitude: pLat, pickupLongitude: pLng, dropoffLatitude: dLat, dropoffLongitude: dLng } = d || {};
            if (pLat != null && pLng != null && dLat != null && dLng != null) {
                apiService.get(API_ENDPOINTS.FLEET_PANEL.ROUTE_GEOMETRY(pLat, pLng, dLat, dLng))
                    .then(r => { if (r.success && Array.isArray(r.data) && r.data.length > 1) setRoutePoints(r.data); })
                    .catch(() => {});
            }
        } catch (e) { } finally {
            setDetailLoading(false);
        }
    };

    const openAssign = (row) => {
        setAssignModal(row);
        setSelectedCourierId(row.assignedCourierId || '');
        loadSuggestions(row.id, row.assignedCourierId);
    };

    const loadSuggestions = useCallback(async (deliveryId, currentCourierId) => {
        setSuggestions([]);
        setSuggestionsLoading(true);
        try {
            const response = await apiService.get(API_ENDPOINTS.FLEET_PANEL.DELIVERY_ASSIGNMENT_SUGGESTIONS(deliveryId));
            if (response.success && Array.isArray(response.data)) {
                setSuggestions(response.data);
                // Mevcut kurye yoksa sistem önerisini (sıradaki) ön-seç
                if (!currentCourierId) {
                    const suggested = response.data.find(s => s.isSuggested);
                    if (suggested) setSelectedCourierId(suggested.courierId);
                }
            }
        } catch (e) { } finally {
            setSuggestionsLoading(false);
        }
    }, []);

    const handleAssignCourier = async () => {
        if (!assignModal || !selectedCourierId) return;
        try {
            setActionLoading(assignModal.id);
            const response = await apiService.put(API_ENDPOINTS.FLEET_PANEL.DELIVERY_ASSIGN_COURIER, {
                deliveryId: assignModal.id,
                courierId: selectedCourierId,
            });
            if (response.success) {
                fleetToast.success('Kurye başarıyla atandı');
                setAssignModal(null);
                setSelectedCourierId('');
                setSuggestions([]);
                loadDeliveries(activeTab);
                loadTabCounts();
                loadCritical();
                /* Kendi işlemimiz karşı taraftan push üretmiyor; sol menü rozeti bu olayla
                   tazelenir (katılma talebi rozetiyle aynı desen). */
                emitFleetAppEvent(FLEET_APP_EVENTS.DELIVERIES_CHANGED);
            } else {
                fleetToast.error(response.error || response.message || 'Kurye atama başarısız');
            }
        } catch (e) {
            fleetToast.error('Kurye atama sırasında hata oluştu');
        } finally {
            setActionLoading(null);
        }
    };

    const renderActions = (row) => {
        return (
            <div className="delivery-actions">
                <button
                    className="action-btn view"
                    onClick={(e) => { e.stopPropagation(); openDetail(row); }}
                    title="Detay"
                >
                    <Eye size={16} />
                </button>

                {/* canManage=false: paketi yalnız VARIŞ bölgesinden görüyoruz. Teslimatın
                    kendi bölgemize geldiğini biliriz ama operasyonu alma bölgesinin
                    sorumlusundadır — atama düğmesi kapalıdır. Asıl kapı sunucudadır. */}
                <button
                    className="action-btn assign"
                    onClick={(e) => { e.stopPropagation(); openAssign(row); }}
                    title={row.canManage === false
                        ? 'Bu paket bölgenize teslim edilecek; operasyonu alma bölgesinin sorumlusundadır'
                        : 'Kurye Ata / Transfer'}
                    disabled={isTerminal(row.statusCode) || row.canManage === false}
                >
                    <UserCheck size={16} />
                </button>
            </div>
        );
    };

    const columns = useMemo(() => [
        {
            field: 'externalOrderId',
            header: 'Sipariş No',
            sortable: true,
            filterable: true,
            render: (row) => (
                <div className="order-id-cell">
                    {/* Kanal rozeti sipariş numarasının yanında: numara zaten satırın kimliği,
                        paketin nereden geldiği de onunla birlikte okunur. */}
                    <MarketplaceBadge code={row.marketplaceCode} integration={row.isIntegrationChannel} />
                    <span className="order-id-primary">{orderNo(row)}</span>
                    {/* Paket bölgemize TESLİM EDİLECEK ama operasyonu bizde değil: neden hiçbir
                        işlem yapamadığımız satırda yazsın, kullanıcı düğmeyi arayıp durmasın. */}
                    {row.canManage === false && (
                        <span className="order-id-readonly" title="Operasyonu alma bölgesinin sorumlusundadır">
                            bölgene teslim
                        </span>
                    )}
                </div>
            )
        },
        {
            field: 'partnerName',
            header: 'Restoran',
            sortable: true,
            filterable: true,
            render: (row) => (
                <div className="partner-cell">
                    <span>{row.partnerName || '-'}</span>
                    {row.crossPickupExpectedPickupByUtc && (
                        <span
                            className="cross-pickup-badge"
                            title="Çapraz alım: kurye bu paketi komşu restorandaki paketle birlikte alıyor"
                        >
                            Çapraz alım
                        </span>
                    )}
                </div>
            )
        },
        {
            field: 'assignedCourierName',
            header: 'Kurye',
            sortable: true,
            filterable: true,
            render: (row) => row.assignedCourierName || <span className="text-muted">Atanmadı</span>
        },
        {
            field: 'statusCode',
            header: 'Durum',
            sortable: true,
            render: (row) => {
                const status = STATUS_MAP[row.statusCode] || { label: 'Bilinmiyor', tone: 'unknown' };
                const delay = computeDelay(row);
                return (
                    <div className="status-cell">
                        <span className={`status-badge ${status.tone}`}>{status.label}</span>
                        {delay && (
                            <span className={`delay-badge ${delay.tone}`} title="Gecikme hatırlatması">
                                <AlertTriangle size={11} /> {delay.label}
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            field: 'distanceMeters',
            header: 'Mesafe',
            sortable: true,
            render: (row) => formatDistance(row.distanceMeters)
        },
        {
            field: 'promisedAtUtc',
            header: 'Taahhüt',
            sortable: true,
            render: (row) => formatDate(row.promisedAtUtc)
        },
        {
            field: 'createdAtUtc',
            header: 'Oluşturulma',
            sortable: true,
            render: (row) => formatDate(row.createdAtUtc)
        }
    ], [nowTick]);

    const handleCardAction = (e, action, delivery) => {
        e.preventDefault();
        e.stopPropagation();
        action(delivery);
    };

    const renderCard = (delivery) => {
        const status = STATUS_MAP[delivery.statusCode] || { label: 'Bilinmiyor', tone: 'unknown' };
        const delay = computeDelay(delivery);

        return (
            <div className={`delivery-card${delay ? ' has-delay' : ''}`}>
                <div className="delivery-card-header">
                    <div className="delivery-card-top">
                        <div
                            className="delivery-card-title"
                            onClick={(e) => handleCardAction(e, openDetail, delivery)}
                        >
                            <h3>{delivery.partnerName || 'Restoran'}</h3>
                            <p className="order-no">
                                <MarketplaceBadge code={delivery.marketplaceCode} integration={delivery.isIntegrationChannel} />
                                {orderNo(delivery)}
                            </p>
                        </div>
                        <div className="delivery-card-status">
                            <span className={`status-badge ${status.tone}`}>{status.label}</span>
                            {delay && (
                                <span className={`delay-badge ${delay.tone}`}>
                                    <AlertTriangle size={11} /> {delay.label}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="delivery-card-actions">
                        <button
                            className="card-action-btn secondary"
                            onClick={(e) => handleCardAction(e, openDetail, delivery)}
                        >
                            <Eye size={16} />
                            <span>Detay</span>
                        </button>

                        <button
                            className="card-action-btn success"
                            onClick={(e) => {
                                if (!isTerminal(delivery.statusCode) && delivery.canManage !== false) {
                                    handleCardAction(e, openAssign, delivery);
                                }
                            }}
                            disabled={isTerminal(delivery.statusCode) || delivery.canManage === false}
                        >
                            <UserCheck size={16} />
                            <span>Kurye Ata / Transfer</span>
                        </button>
                    </div>
                </div>

                <div className="delivery-card-body">
                    <div className="delivery-card-field">
                        <label>Kurye</label>
                        <span>{delivery.assignedCourierName || '-'}</span>
                    </div>
                    <div className="delivery-card-field">
                        <label>Mesafe</label>
                        <span>{formatDistance(delivery.distanceMeters)}</span>
                    </div>
                    <div className="delivery-card-field">
                        <label>Taahhüt</label>
                        <span>{formatDate(delivery.promisedAtUtc)}</span>
                    </div>
                    <div className="delivery-card-field">
                        <label>Tarih</label>
                        <span>{formatDate(delivery.createdAtUtc)}</span>
                    </div>
                </div>
            </div>
        );
    };

    // Kritik satırları (atanmamış/gecikmiş) sayfa içinde en üste sabitle — gözden kaçmasın.
    const sortedDeliveries = useMemo(() => {
        const sev = (row) => {
            const d = computeDelay(row);
            if (!d) return 0;
            return d.kind === 3 ? 3 : d.kind === 1 ? 2 : 1;
        };
        return deliveries
            .map((row, i) => ({ row, i, s: sev(row) }))
            .sort((a, b) => (b.s - a.s) || (a.i - b.i))
            .map(x => x.row);
    }, [deliveries, nowTick]);

    const statusOptions = useMemo(() => {
        return [
            { label: 'Tümü', value: '' },
            ...Object.entries(STATUS_MAP).map(([key, val]) => ({ label: val.label, value: key }))
        ];
    }, []);

    /* Kurye seçenekleri filo üyelerinden gelir (atama modalıyla aynı liste). "Atanmamış"
       ayrı bir seçenek: hangi paketlerin kuryesiz kaldığını tek tıkla görmek en sık ihtiyaç. */
    const courierOptions = useMemo(() => {
        const list = members
            .filter(m => m.courierId)
            .map(m => ({ value: m.courierId, label: m.courierName || m.courierPhone || 'İsimsiz kurye' }))
            .sort((a, b) => a.label.localeCompare(b.label, 'tr'));

        return [
            { label: 'Tüm kuryeler', value: '' },
            { label: 'Atanmamış', value: 'unassigned' },
            ...list
        ];
    }, [members]);

    /* Atanamayan paketlerin son atama hatası (backend: DispatchFailureLog). Sebep yazılmayan
       kayıtlar atlanır — boş satır göstermek yanıltıcı olur. */
    const criticalUnassignedReasons = useMemo(() => {
        return (critical?.items || [])
            .filter(i => i.kind === 3 && i.lastFailureReason)
            .slice(0, 5)
            .map(i => ({
                id: i.id,
                orderNo: i.externalOrderId || i.internalOrderNumber || '-',
                reason: i.lastFailureReason
            }));
    }, [critical]);

    useEffect(() => {
        loadDeliveries(activeTab);
    }, [activeTab, currentPage, pageSize, sortField, sortOrder, filters, advancedFilters]);

    useEffect(() => {
        loadTabCounts();
        loadCritical();
    }, [loadTabCounts, loadCritical]);

    useEffect(() => {
        loadMembers();
        loadPartnerOptions();
    }, [loadMembers, loadPartnerOptions]);

    // Yoğun akışta bir siparişin ~5 statü olayı × N eşzamanlı sipariş = kısa pencerede yüzlerce olay gelir.
    // Her olayda ayrı full-reload yerine olayları 400ms penceresinde birleştir (coalesce) → tek fetch.
    // Nihai sonuç aynı (burst bittikten sonra liste tazelenir), sadece gereksiz storm önlenir.
    const refreshTimerRef = useRef(null);
    useEffect(() => {
        const handler = () => {
            if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = setTimeout(() => {
                loadDeliveries(activeTab);
                loadTabCounts();
                loadCritical();
            }, 400);
        };
        window.addEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_DELIVERY_UPDATE_NOTIFICATION}`, handler);
        return () => {
            window.removeEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_DELIVERY_UPDATE_NOTIFICATION}`, handler);
            if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        };
    }, [loadDeliveries, loadTabCounts, loadCritical, activeTab]);

    /* Gecikme hatırlatmasının toast'ı BURADA DEĞİL: fleetNotificationRouter bu tipi
       (DelayReminder) sahiplendi ve toast + masaüstü bildirimi + ses + zil kaydını tek
       yerden üretiyor. Buradaki yerel toast korunsaydı, filo yöneticisi varsayılan çalışma
       ekranı olan Teslimatlar sayfasındayken her hatırlatmada İKİ toast görürdü.
       Yukarıdaki 400 ms'lik birleştirilmiş veri tazeleme efekti bu kanalda kalmaya devam
       ediyor — o bildirim değil, liste yenilemesi. */

    // Gecikme rozeti sayaçlarını canlı tut (60sn).
    useEffect(() => {
        const id = setInterval(() => setNowTick(Date.now()), 60000);
        return () => clearInterval(id);
    }, []);

    return (
        <div className="deliveries-list-page">
            <div className="page-header">
                <div className="header-left">
                    <div className="page-title">
                        <h1>Teslimatlar</h1>
                        <span className="count-badge">
                            {totalRecords}
                        </span>
                    </div>
                </div>

                <div className="header-right">
                    <button
                        className={`btn btn-outline ${showAdvancedFilters ? 'active' : ''}`}
                        onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                    >
                        <Filter size={18} />
                        Filtreler
                    </button>
                    <button className="btn btn-secondary" onClick={() => { loadDeliveries(activeTab); loadTabCounts(); loadCritical(); }} disabled={loading}>
                        <RefreshCw size={18} className={loading ? 'spinning' : ''} />
                        Yenile
                    </button>
                </div>
            </div>

            <div className="deliveries-tabs">
                {TAB_CONFIG.map((tab) => {
                    const count = tabCounts[tab.key] ?? 0;
                    const isActive = activeTab === tab.key;

                    return (
                        <button
                            key={tab.key}
                            className={`delivery-tab ${isActive ? 'active' : ''}`}
                            onClick={() => handleTabChange(tab.key)}
                        >
                            <span className="tab-label">{tab.label}</span>
                            <span className="tab-count">{count}</span>
                        </button>
                    );
                })}
            </div>

            {critical && critical.totalCritical > 0 && (
                <div className="deliveries-critical-strip">
                    <AlertTriangle size={15} className="dcs-icon" />
                    <span className="dcs-title">Dikkat gereken paketler:</span>
                    {critical.unassignedCount > 0 && (
                        <span className="dcs-tag danger">{critical.unassignedCount} atanmamış</span>
                    )}
                    {critical.overdueCount > 0 && (
                        <span className="dcs-tag warn">{critical.overdueCount} gecikmiş</span>
                    )}
                    <span className="dcs-hint">en üste sıralandı</span>
                </div>
            )}

            {/* Atanamama SEBEBİ: "atanmadı" uyarısı tek başına bakiye yetersizliğini, bölge ataması
                eksikliğini ve gerçekten kurye olmamasını ayırt ettirmiyordu. */}
            {criticalUnassignedReasons.length > 0 && (
                <div className="deliveries-critical-strip dcs-reasons">
                    <Info size={15} className="dcs-icon" />
                    <span className="dcs-title">Atanamama sebebi:</span>
                    <div className="dcs-reason-list">
                        {criticalUnassignedReasons.map((r) => (
                            <span key={r.id} className="dcs-reason">
                                <strong>{r.orderNo}</strong> — {r.reason}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {showAdvancedFilters && (
                <div className="advanced-filters">
                    <div className="filters-grid">
                        <div className="filter-group">
                            <label>Arama</label>
                            <input
                                type="text"
                                placeholder="Sipariş No, Partner, Kurye..."
                                value={advancedFilters.searchTerm}
                                onChange={(e) => handleAdvancedFilterChange('searchTerm', e.target.value)}
                            />
                        </div>

                        <div className="filter-group">
                            <label>Kurye</label>
                            <select
                                value={advancedFilters.courierId}
                                onChange={(e) => handleAdvancedFilterChange('courierId', e.target.value)}
                            >
                                {courierOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Restoran</label>
                            <select
                                value={advancedFilters.partnerId}
                                onChange={(e) => handleAdvancedFilterChange('partnerId', e.target.value)}
                                disabled={partnerOptions.length === 0}
                            >
                                <option value="">
                                    {partnerOptions.length === 0 ? 'Restoran kaydı yok' : 'Tüm restoranlar'}
                                </option>
                                {partnerOptions.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.name || 'Adsız restoran'} ({p.deliveryCount})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Durum</label>
                            <select
                                value={advancedFilters.status}
                                onChange={(e) => handleAdvancedFilterChange('status', e.target.value)}
                            >
                                {statusOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Başlangıç Tarihi</label>
                            <input
                                type="datetime-local"
                                value={advancedFilters.startDate}
                                onChange={(e) => handleAdvancedFilterChange('startDate', e.target.value)}
                            />
                        </div>

                        <div className="filter-group">
                            <label>Bitiş Tarihi</label>
                            <input
                                type="datetime-local"
                                value={advancedFilters.endDate}
                                onChange={(e) => handleAdvancedFilterChange('endDate', e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="filters-actions">
                        <button className="btn btn-secondary" onClick={clearAdvancedFilters}>
                            Temizle
                        </button>
                        <button className="btn btn-primary" onClick={applyAdvancedFilters}>
                            Uygula
                        </button>
                    </div>
                </div>
            )}

            <DataGrid
                data={sortedDeliveries}
                columns={columns}
                loading={loading}
                error={error}
                currentPage={currentPage}
                pageSize={pageSize}
                totalRecords={totalRecords}
                onPageChange={setCurrentPage}
                onPageSizeChange={handlePageSizeChange}
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={handleSort}
                filters={filters}
                filterTypes={filterTypes}
                onFilterChange={handleFilterChange}
                onRowDoubleClick={openDetail}
                renderActions={renderActions}
                renderCard={renderCard}
                emptyIcon={Package}
                emptyMessage="Teslimat bulunmuyor"
            />

            {selectedDelivery && (
                <div className="modal-overlay" onClick={() => setSelectedDelivery(null)}>
                    <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Teslimat Detayı</h2>
                            <button className="modal-close" onClick={() => setSelectedDelivery(null)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            {detailLoading ? (
                                <div className="page-loading"><Loader2 className="spinning" size={32} /></div>
                            ) : (
                                <>
                                <div className="fdl-detail-grid">
                                    <div className="fdl-detail-item">
                                        <Package size={16} />
                                        <span className="fdl-detail-label">Sipariş No</span>
                                        <span className="fdl-detail-value">{orderNo(selectedDelivery)}</span>
                                    </div>
                                    <div className="fdl-detail-item">
                                        <User size={16} />
                                        <span className="fdl-detail-label">Restoran</span>
                                        <span className="fdl-detail-value">{selectedDelivery.partnerName || '-'}</span>
                                    </div>
                                    {getDeliveryChannel(selectedDelivery) && (
                                        <div className="fdl-detail-item">
                                            <Package size={16} />
                                            <span className="fdl-detail-label">Sipariş kanalı</span>
                                            <span className="fdl-detail-value">
                                                <MarketplaceBadge
                                                    code={selectedDelivery.marketplaceCode}
                                                    integration={selectedDelivery.isIntegrationChannel}
                                                    size="md"
                                                    withLabel
                                                />
                                            </span>
                                        </div>
                                    )}
                                    {/* Dış POS paketinde ücret restorandan filoya doğrudan gidiyor;
                                        panelde tutar göremeyen kullanıcı eksik kayıt sanmasın. */}
                                    {selectedDelivery.isIntegrationChannel && (
                                        <div className="fdl-detail-item fdl-detail-note">
                                            <Info size={16} />
                                            <span className="fdl-detail-label">Ücretlendirme</span>
                                            <span className="fdl-detail-value">
                                                Paket bedeli restoranla aranızda; Hızlıyo bu tutara girmez.
                                            </span>
                                        </div>
                                    )}
                                    <div className="fdl-detail-item">
                                        <User size={16} />
                                        <span className="fdl-detail-label">Kurye</span>
                                        <span className="fdl-detail-value">{selectedDelivery.assignedCourierName || 'Atanmadı'}</span>
                                    </div>
                                    <div className="fdl-detail-item">
                                        <Package size={16} />
                                        <span className="fdl-detail-label">Durum</span>
                                        <span className={`status-badge ${(STATUS_MAP[selectedDelivery.statusCode] || {}).tone || ''}`}>
                                            {(STATUS_MAP[selectedDelivery.statusCode] || {}).label || selectedDelivery.status}
                                        </span>
                                    </div>
                                    <div className="fdl-detail-item">
                                        <Navigation size={16} />
                                        <span className="fdl-detail-label">Mesafe</span>
                                        <span className="fdl-detail-value">{formatDistance(selectedDelivery.distanceMeters)}</span>
                                    </div>
                                    <div className="fdl-detail-item">
                                        <Clock size={16} />
                                        <span className="fdl-detail-label">Taahhüt</span>
                                        <span className="fdl-detail-value">{formatDate(selectedDelivery.promisedAtUtc)}</span>
                                    </div>
                                    <div className="fdl-detail-item">
                                        <Clock size={16} />
                                        <span className="fdl-detail-label">Atanma</span>
                                        <span className="fdl-detail-value">{formatDate(selectedDelivery.assignedAtUtc)}</span>
                                    </div>
                                    <div className="fdl-detail-item">
                                        <Clock size={16} />
                                        <span className="fdl-detail-label">Teslim Alınma</span>
                                        <span className="fdl-detail-value">{formatDate(selectedDelivery.pickedUpAtUtc)}</span>
                                    </div>
                                    <div className="fdl-detail-item">
                                        <Clock size={16} />
                                        <span className="fdl-detail-label">Teslim Edilme</span>
                                        <span className="fdl-detail-value">{formatDate(selectedDelivery.deliveredAtUtc)}</span>
                                    </div>
                                    <div className="fdl-detail-item">
                                        <MapPin size={16} />
                                        <span className="fdl-detail-label">Teslim Alma</span>
                                        {selectedDelivery.pickupLatitude != null && selectedDelivery.pickupLongitude != null ? (
                                            <>
                                                <MiniLocationMap
                                                    position={[selectedDelivery.pickupLatitude, selectedDelivery.pickupLongitude]}
                                                    color="#f59e0b"
                                                    label="A"
                                                />
                                                <span className="mlm-coord">
                                                    {selectedDelivery.pickupLatitude.toFixed(5)}, {selectedDelivery.pickupLongitude.toFixed(5)}
                                                </span>
                                            </>
                                        ) : (
                                            <span className="fdl-detail-value">-</span>
                                        )}
                                    </div>
                                    <div className="fdl-detail-item">
                                        <MapPin size={16} />
                                        <span className="fdl-detail-label">Teslim Noktası</span>
                                        {selectedDelivery.dropoffLatitude != null && selectedDelivery.dropoffLongitude != null ? (
                                            <>
                                                <MiniLocationMap
                                                    position={[selectedDelivery.dropoffLatitude, selectedDelivery.dropoffLongitude]}
                                                    color="#10b981"
                                                    label="T"
                                                />
                                                <span className="mlm-coord">
                                                    {selectedDelivery.dropoffLatitude.toFixed(5)}, {selectedDelivery.dropoffLongitude.toFixed(5)}
                                                </span>
                                            </>
                                        ) : (
                                            <span className="fdl-detail-value">-</span>
                                        )}
                                    </div>
                                    {selectedDelivery.failureReason && (
                                        <div className="fdl-detail-item fdl-detail-full">
                                            <Ban size={16} />
                                            <span className="fdl-detail-label">Başarısızlık / İptal Sebebi</span>
                                            <span className="fdl-detail-value">{selectedDelivery.failureReason}</span>
                                        </div>
                                    )}
                                    {(selectedDelivery.baseFeeSnapshot != null || selectedDelivery.costPerKmSnapshot != null) && (
                                        <>
                                            <div className="fdl-detail-item">
                                                <span className="fdl-detail-label">Baz Ücret</span>
                                                <span className="fdl-detail-value">{formatCurrency(selectedDelivery.baseFeeSnapshot)}</span>
                                            </div>
                                            <div className="fdl-detail-item">
                                                <span className="fdl-detail-label">Km Başı Ücret</span>
                                                <span className="fdl-detail-value">{formatCurrency(selectedDelivery.costPerKmSnapshot)}</span>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {(selectedDelivery.pickupLatitude != null && selectedDelivery.dropoffLatitude != null) && (
                                    <div className="fdl-timeline-section">
                                        <h3 className="fdl-section-title"><MapPin size={16} /> Rota Haritası</h3>
                                        <DeliveryRouteMap
                                            pickup={[selectedDelivery.pickupLatitude, selectedDelivery.pickupLongitude]}
                                            drop={[selectedDelivery.dropoffLatitude, selectedDelivery.dropoffLongitude]}
                                            route={routePoints}
                                            distanceMeters={selectedDelivery.distanceMeters}
                                        />
                                    </div>
                                )}

                                {timelineSteps.length > 0 && (
                                    <div className="fdl-timeline-section">
                                        <h3 className="fdl-section-title"><Truck size={16} /> Operasyon Durumu</h3>
                                        <div className="fdl-timeline">
                                            {timelineSteps.map((step) => (
                                                <div key={step.st} className={`fdl-tl-item ${step.state}${step.isLast ? ' last' : ''}`}>
                                                    <div className="fdl-tl-icon"><step.Icon size={14} /></div>
                                                    <div className="fdl-tl-content">
                                                        <div className="fdl-tl-label">{step.label}</div>
                                                        <div className="fdl-tl-time">{formatDate(step.time)}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {(selectedDelivery.history || []).length > 0 && (
                                    <div className="fdl-timeline-section">
                                        <h3 className="fdl-section-title"><History size={16} /> Durum Geçmişi</h3>
                                        <div className="fdl-history-list">
                                            {selectedDelivery.history.map((h, idx) => (
                                                <div key={h.id || idx} className="fdl-history-item">
                                                    <div className="fdl-history-icon">
                                                        <RefreshCw size={14} />
                                                    </div>
                                                    <div className="fdl-history-content">
                                                        <div className="fdl-history-transition">
                                                            <span className="fdl-history-badge">{(STATUS_MAP[h.fromStatus] || {}).label || `Durum ${h.fromStatus}`}</span>
                                                            <ArrowRight size={12} />
                                                            <span className="fdl-history-badge">{(STATUS_MAP[h.toStatus] || {}).label || `Durum ${h.toStatus}`}</span>
                                                        </div>
                                                        <div className="fdl-history-meta">
                                                            <span className="fdl-history-time">{formatDate(h.changedAtUtc)}</span>
                                                            {h.changedBy && <span className="fdl-history-by">{h.changedBy}</span>}
                                                        </div>
                                                        {h.note && <div className="fdl-history-note">{h.note}</div>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {assignModal && (
                <div className="modal-overlay" onClick={() => { setAssignModal(null); setSelectedCourierId(''); }}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Kurye Ata</h2>
                            <button className="modal-close" onClick={() => { setAssignModal(null); setSelectedCourierId(''); }}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="fdl-assign-info">
                                <p className="fdl-assign-order">
                                    <strong>Sipariş:</strong>
                                    <MarketplaceBadge code={assignModal.marketplaceCode} integration={assignModal.isIntegrationChannel} />
                                    {orderNo(assignModal)}
                                </p>
                                <p><strong>Restoran:</strong> {assignModal.partnerName || '-'}</p>
                                {assignModal.assignedCourierName && (
                                    <p><strong>Mevcut Kurye:</strong> {assignModal.assignedCourierName}</p>
                                )}
                            </div>
                            <div className="fdl-assign-select-wrapper">
                                <label className="fdl-assign-label">
                                    Kurye Seçin <span className="fdl-assign-label-hint">(adalet sıralı — en uygun üstte)</span>
                                </label>

                                {suggestionsLoading ? (
                                    <div className="fdl-suggest-loading">
                                        <Loader2 size={16} className="spinning" /> Öneriler hesaplanıyor…
                                    </div>
                                ) : suggestions.length > 0 ? (
                                    <div className="fdl-suggest-list">
                                        {suggestions.map(s => {
                                            const selectable = s.hasLocation && !s.atCapacity;
                                            const selected = selectedCourierId === s.courierId;
                                            const idleLabel = s.minutesIdle >= 9999 ? 'hiç atanmadı' : `${s.minutesIdle} dk boşta`;
                                            return (
                                                <button
                                                    type="button"
                                                    key={s.courierId}
                                                    className={`fdl-suggest-item${selected ? ' selected' : ''}${!selectable ? ' disabled' : ''}`}
                                                    disabled={!selectable}
                                                    onClick={() => selectable && setSelectedCourierId(s.courierId)}
                                                >
                                                    <div className="fdl-suggest-radio">
                                                        <span className={`fdl-radio-dot${selected ? ' on' : ''}`} />
                                                    </div>
                                                    <div className="fdl-suggest-main">
                                                        <div className="fdl-suggest-name-row">
                                                            <span className="fdl-suggest-name">
                                                                {s.courierName || 'İsimsiz'}
                                                            </span>
                                                            {s.isSuggested && (
                                                                <span className="fdl-badge fdl-badge-suggested">
                                                                    <Award size={12} /> Önerilen • sıradaki
                                                                </span>
                                                            )}
                                                            {!s.hasLocation && (
                                                                <span className="fdl-badge fdl-badge-warn">konum yok</span>
                                                            )}
                                                            {s.atCapacity && (
                                                                <span className="fdl-badge fdl-badge-warn">limit dolu</span>
                                                            )}
                                                        </div>
                                                        <div className="fdl-suggest-meta">
                                                            {s.phone && <span>{s.phone}</span>}
                                                            {s.distanceKm != null && (
                                                                <span><MapPin size={11} /> {s.distanceKm.toFixed(1)} km</span>
                                                            )}
                                                            <span><Clock size={11} /> {idleLabel}</span>
                                                            <span><Truck size={11} /> bugün {s.todayDeliveries} teslimat</span>
                                                            <span><Zap size={11} /> {s.activeDeliveries} aktif</span>
                                                        </div>
                                                    </div>
                                                    {s.score != null && (
                                                        <div className="fdl-suggest-score" title="Adalet skoru (yüksek = daha uygun)">
                                                            {Math.round(s.score)}
                                                        </div>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="fdl-select-container">
                                        <select
                                            className="fdl-assign-select"
                                            value={selectedCourierId}
                                            onChange={(e) => setSelectedCourierId(e.target.value)}
                                        >
                                            <option value="">-- Kurye seçin --</option>
                                            {members.map(m => (
                                                <option key={m.courierId} value={m.courierId}>
                                                    {m.courierName || 'İsimsiz'} {m.courierPhone ? `(${m.courierPhone})` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown size={16} className="fdl-select-icon" />
                                    </div>
                                )}
                            </div>
                            <div className="modal-actions">
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => { setAssignModal(null); setSelectedCourierId(''); }}
                                >
                                    Vazgeç
                                </button>
                                <button
                                    className="btn btn-primary"
                                    disabled={!selectedCourierId || actionLoading === assignModal.id}
                                    onClick={handleAssignCourier}
                                >
                                    {actionLoading === assignModal.id ? <Loader2 size={16} className="spinning" /> : <UserCheck size={16} />}
                                    Ata
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default DeliveriesPage;
